import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";

import GlobalFilterBar, { FilterState, PrimarySort } from "./GlobalFilterBar";
import {
  PAGE_TYPE_BUCKETS,
  resolveActiveBucket,
  typesForBucket,
} from "utils/messageBuckets";

/**
 * Only createdAt is indexed on the message search. Severity and author need
 * index fields that do not exist yet, so they are surfaced as disabled rather
 * than quietly ignored.
 */
const UNAVAILABLE_SORTS: PrimarySort[] = ["severity", "author"];

/**
 * The page filter bar, wired to the store.
 *
 * The bar sits in the right-hand column, which swaps between the map and the
 * filtered result depending on the bucket. Both of those mount this component,
 * so the pill that caused the swap is still on screen afterwards and the reader
 * can get back to All. Every piece of its state lives in the store — the bar
 * remounting across that swap must not lose the selection.
 */
const PageFilterBar: FC = () => {
  const dispatch = useDispatch();
  const { messageFilter, messageBucket, messageSort } = useSelector(
    (state: RootState) => state.message
  );

  /**
   * The pill bar and the funnel popup are two views of one filter: both write
   * SET_MESSAGE_FILTER, so selecting a pill also ticks that type in the popup
   * and neither can drift out of step with the query.
   */
  const activeBucket = resolveActiveBucket(
    messageBucket || "",
    messageFilter || []
  );

  const handleFilterChange = (next: FilterState) => {
    dispatch({ type: "SET_MESSAGE_BUCKET", payload: next.type });
    dispatch({
      type: "SET_MESSAGE_FILTER",
      payload: typesForBucket(next.type),
    });

    /**
     * Sorting is the one control here that still has to reach the server, and
     * re-running the query resets the stream. The bar reports its whole state
     * on every change, so dispatching this unconditionally would refetch on a
     * plain pill click too — which is exactly the centre-column flicker the
     * filter is meant to have stopped causing.
     */
    const sortChanged =
      next.primarySort !== messageSort?.primary ||
      next.secondarySort !== messageSort?.secondary;
    if (sortChanged) {
      dispatch({
        type: "SET_MESSAGE_SORT",
        payload: { primary: next.primarySort, secondary: next.secondarySort },
      });
    }
  };

  return (
    <GlobalFilterBar
      typeCounts={PAGE_TYPE_BUCKETS}
      unavailableSorts={UNAVAILABLE_SORTS}
      activeType={activeBucket}
      activePrimarySort={messageSort?.primary as PrimarySort}
      activeSecondarySort={messageSort?.secondary as FilterState["secondarySort"]}
      compact
      onChange={handleFilterChange}
    />
  );
};

export default PageFilterBar;
